import { keepPreviousData, queryOptions, skipToken, useQuery } from '@tanstack/react-query';
import { api } from '@/api';
import { DateRangeParams } from '@/api/apis/resources/schedule';
import { scheduleQueryKeys } from '@/queries/resource/schedule/schedule.keys';

export const sScheduleQueries = {
  planningProcess: (processId?: string | null) =>
    queryOptions({
      queryKey: scheduleQueryKeys.s.getPlanningProcessKey(processId),
      queryFn: processId ? () => api.schedule.s.getSPlanningProcessById(processId) : skipToken,
    }),
  validation: (scheduleId?: string) =>
    queryOptions({
      queryKey: scheduleQueryKeys.s.getValidationKey(scheduleId),
      queryFn: scheduleId ? () => api.schedule.s.getSValidation(scheduleId) : skipToken,
    }),
  hrSystemConflicts: (scheduleId?: string) =>
    queryOptions({
      queryKey: scheduleQueryKeys.s.getHrSystemConflictsKey(scheduleId),
      queryFn: scheduleId ? () => api.schedule.s.getSScheduleHrSystemConflicts(scheduleId) : skipToken,
    }),
};

export const sLiScheduleQueries = {
  shifts: (dateRange: DateRangeParams, processId?: string | null) => {
    const queryFn = () =>
      processId ? api.schedule.s.getSPlanningShifts(processId, dateRange) : api.schedule.s.getSLiShifts(dateRange);

    return queryOptions({
      queryKey: scheduleQueryKeys.sLi.getShiftsKey(dateRange, processId),
      queryFn: () => queryFn().then((data) => data.resourcesShifts),
    });
  },
  regions: () =>
    queryOptions({
      queryKey: scheduleQueryKeys.sLi.getRegionsKey(),
      queryFn: () => api.schedule.s.getSLiRegions(),
    }),
  schedule: (startDayOfMonth: Date) =>
    queryOptions({
      queryKey: scheduleQueryKeys.sLi.getScheduleKey(startDayOfMonth),
      queryFn: () => api.schedule.s.getSLiSchedule(startDayOfMonth),
    }),
  rules: () =>
    queryOptions({
      queryKey: scheduleQueryKeys.sLi.getRulesKey(),
      queryFn: () => api.schedule.s.getSLiScheduleRules(),
    }),
  inputs: () =>
    queryOptions({
      queryKey: scheduleQueryKeys.sLi.getInputsKey(),
      queryFn: () => api.schedule.s.getSLiScheduleInputs(),
    }),
};

export const sHtLuScheduleQueries = {
  shifts: (dateRange: DateRangeParams, processId?: string | null) => {
    const queryFn = () =>
      processId ? api.schedule.s.getSPlanningShifts(processId, dateRange) : api.schedule.s.getSHtLuShifts(dateRange);

    return queryOptions({
      queryKey: scheduleQueryKeys.sHtLu.getShiftsKey(dateRange, processId),
      queryFn: () => queryFn().then((data) => data.resourcesShifts),
    });
  },
  regions: () =>
    queryOptions({
      queryKey: scheduleQueryKeys.sHtLu.getRegionsKey(),
      queryFn: () => api.schedule.s.getSHtLuRegions(),
    }),
  schedule: (startDayOfMonth: Date) =>
    queryOptions({
      queryKey: scheduleQueryKeys.sHtLu.getScheduleKey(startDayOfMonth),
      queryFn: () => api.schedule.s.getSHtLuSchedule(startDayOfMonth),
    }),
  rules: () =>
    queryOptions({
      queryKey: scheduleQueryKeys.sHtLu.getRulesKey(),
      queryFn: () => api.schedule.s.getSHtLuScheduleRules(),
    }),
  inputs: () =>
    queryOptions({
      queryKey: scheduleQueryKeys.sHtLu.getInputsKey(),
      queryFn: () => api.schedule.s.getSHtLuScheduleInputs(),
    }),
};

export const useSLiScheduleShiftsQuery = (dateRange: DateRangeParams, processId?: string | null, isPlanned = false) => {
  return useQuery({
    ...sLiScheduleQueries.shifts(dateRange, isPlanned ? processId : undefined),
    placeholderData: keepPreviousData,
  });
};
export const useSHtLuScheduleShiftsQuery = (
  dateRange: DateRangeParams,
  processId?: string | null,
  isPlanned = false,
) => {
  return useQuery({
    ...sHtLuScheduleQueries.shifts(dateRange, isPlanned ? processId : undefined),
    placeholderData: keepPreviousData,
  });
};

export const useSLiScheduleRegionsQuery = () => {
  return useQuery({
    ...sLiScheduleQueries.regions(),
    staleTime: 30 * 60 * 1000, // 30 min
    placeholderData: keepPreviousData,
  });
};
export const useSHtLuScheduleRegionsQuery = () => {
  return useQuery({
    ...sHtLuScheduleQueries.regions(),
    staleTime: 30 * 60 * 1000, // 30 min
    placeholderData: keepPreviousData,
  });
};

export const useSLiMonthScheduleQuery = (startDayOfMonth: Date, refetchTime?: number) => {
  return useQuery({
    ...sLiScheduleQueries.schedule(startDayOfMonth),
    refetchInterval: refetchTime,
  });
};
export const useSHtLuMonthScheduleQuery = (startDayOfMonth: Date, refetchTime?: number) => {
  return useQuery({
    ...sHtLuScheduleQueries.schedule(startDayOfMonth),
    refetchInterval: refetchTime,
  });
};

export const useSValidationQuery = (scheduleId?: string, enabled = false) => {
  return useQuery({
    ...sScheduleQueries.validation(scheduleId),
    enabled: Boolean(scheduleId && enabled),
  });
};

export const useSPlanningProcessQuery = (processId?: string | null, enabled = false, refetchTime?: number) => {
  return useQuery({
    ...sScheduleQueries.planningProcess(processId),
    enabled: Boolean(processId && enabled),
    refetchInterval: refetchTime,
  });
};

export const useSLiScheduleSettingsRulesQuery = () => {
  return useQuery(sLiScheduleQueries.rules());
};
export const useSHtLuScheduleSettingsRulesQuery = () => {
  return useQuery(sHtLuScheduleQueries.rules());
};

export const useSLiScheduleSettingsInputsQuery = () => {
  return useQuery(sLiScheduleQueries.inputs());
};
export const useSHtLuScheduleSettingsInputsQuery = () => {
  return useQuery(sHtLuScheduleQueries.inputs());
};

export const useSHrSystemConflictsQuery = (scheduleId?: string, enabled = false) => {
  return useQuery({
    ...sScheduleQueries.hrSystemConflicts(scheduleId),
    enabled: Boolean(scheduleId && enabled),
  });
};
